import React, { useState } from "react";
import { Check, ChevronsUpDown, Search, X } from "lucide-react";
import { Button } from "./ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { cn } from "../lib/utils";

// Searchable multi-select: value = array of strings; chips ditampilkan di bawah trigger.
export const MultiSelect = ({ options = [], value = [], onChange, placeholder = "Pilih...", testId }) => {
  const [open, setOpen] = useState(false);
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" role="combobox" aria-expanded={open} data-testid={testId}
            className={cn("w-full justify-between min-h-[44px] rounded-xl font-normal bg-white", value.length === 0 && "text-muted-foreground")}>
            <span className="truncate">{value.length ? `${value.length} dipilih` : placeholder}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0 rounded-xl" align="start">
          <Command>
            <div className="flex items-center border-b px-3"><Search className="mr-2 h-4 w-4 shrink-0 opacity-50" /><CommandInput placeholder="Cari..." className="h-11 border-0 focus:ring-0" /></div>
            <CommandList>
              <CommandEmpty>Tidak ada pilihan.</CommandEmpty>
              <CommandGroup>
                {options.map((opt) => (
                  <CommandItem key={opt} value={opt} onSelect={() => toggle(opt)} className="min-h-[44px]" data-testid={testId ? `${testId}-opt-${opt}` : undefined}>
                    <span className={cn("mr-2 h-4 w-4 rounded border flex items-center justify-center", value.includes(opt) ? "bg-[hsl(224,64%,33%)] border-[hsl(224,64%,33%)] text-white" : "border-slate-300")}>
                      {value.includes(opt) && <Check className="h-3 w-3" />}
                    </span>
                    {opt}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid={testId ? `${testId}-chips` : undefined}>
          {value.map((v) => (
            <span key={v} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full bg-indigo-50 text-indigo-700 font-semibold border border-indigo-100" data-testid={testId ? `${testId}-chip-${v}` : undefined}>
              {v}
              <button type="button" onClick={() => toggle(v)} className="h-5 w-5 -mr-1 rounded-full hover:bg-indigo-100 flex items-center justify-center" aria-label={`Hapus ${v}`} data-testid={testId ? `${testId}-remove-${v}` : undefined}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// Tag input teks bebas (Enter/koma untuk menambah).
export const TagInput = ({ value = [], onChange, placeholder, testId }) => {
  const [text, setText] = useState("");
  const add = () => { const t = text.trim().replace(/,$/, "").trim(); if (t && !value.includes(t)) onChange([...value, t]); setText(""); };
  return (
    <div>
      <div className="flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }} onBlur={add}
          placeholder={placeholder} data-testid={testId} className="flex-1 min-h-[44px] rounded-xl border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        <Button type="button" variant="outline" className="rounded-xl min-h-[44px]" onClick={add} data-testid={testId ? `${testId}-add` : undefined}>Tambah</Button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid={testId ? `${testId}-chips` : undefined}>
          {value.map((v) => (
            <span key={v} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold border border-emerald-100" data-testid={testId ? `${testId}-chip-${v}` : undefined}>
              {v}
              <button type="button" onClick={() => onChange(value.filter((x) => x !== v))} className="h-5 w-5 -mr-1 rounded-full hover:bg-emerald-100 flex items-center justify-center" aria-label={`Hapus ${v}`} data-testid={testId ? `${testId}-remove-${v}` : undefined}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
